import React, { useContext, useMemo } from 'react';
import { Alert, Col, Spin } from 'antd';
import ActionTypes from '@utils/deviceActions';
import { useTranslation } from 'react-i18next';
import CustomTag from '@components/CustomTag';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '../useSwr';

import * as S from './styles';

interface ActionsLimitProps {
  isActive: boolean;
  action: ActionTypes.SUSPENSION | ActionTypes.BROKER_CANCELLATION;
  devices: {
    msisdn: string;
  }[];
}

export function useActionsLimit({
  isActive,
  action,
  devices,
}: ActionsLimitProps) {
  const { t } = useTranslation('actions-modal');
  const { theme } = useContext(SessionContext);

  const { data: actionsLimit, error } = useSwr(
    isActive ? `/service-proxy/actions/get-limit/${action}` : null,
  );

  const isNotFound = useMemo(
    () => error?.response?.status === 404,
    [error?.response?.status],
  );

  const remainingActions = useMemo(
    () => (actionsLimit ? actionsLimit?.limit - actionsLimit?.used : 0),
    [actionsLimit],
  );

  const limitHasBeenReached = useMemo(() => {
    if (isNotFound) return false;
    return remainingActions === 0 || remainingActions < devices?.length;
  }, [isNotFound, remainingActions, devices?.length]);

  const actionsLimitContent = useMemo(() => {
    const selectedDevicesLength = devices?.length;

    if (isNotFound) {
      return (
        <Alert
          description={t('actions-limit.no-usage-limit-found')}
          type="info"
          showIcon
        />
      );
    }

    const limitErrorType = remainingActions ? 'exceeded' : 'reached';

    const limitAlertDescription = t(
      `actions-limit.limit-has-been-${limitErrorType}`,
      {
        count: remainingActions,
        devicesCount: selectedDevicesLength,
      },
    );

    return (
      <>
        <S.ActionsLimitContent>
          {!actionsLimit ? (
            <S.ActionsLimitLoading>
              <Spin />
              <span className="actions-limit-loading-text">
                {t('actions-limit.loading-usage-limit', {
                  count: selectedDevicesLength,
                })}
              </span>
            </S.ActionsLimitLoading>
          ) : (
            <S.ActionsLimitAvailable>
              <p>{t('actions-limit.usage-limit-label')}</p>
              <CustomTag>
                <span
                  style={{
                    color:
                      theme?.colors?.[limitHasBeenReached ? 'red' : 'primary'],
                  }}
                >
                  {selectedDevicesLength}
                </span>
                <span>{` / ${remainingActions}`}</span>
              </CustomTag>
            </S.ActionsLimitAvailable>
          )}
        </S.ActionsLimitContent>

        {limitHasBeenReached && actionsLimit && (
          <Col span={24} style={{ marginTop: 16 }}>
            <Alert description={limitAlertDescription} type="error" showIcon />
          </Col>
        )}
      </>
    );
  }, [
    actionsLimit,
    devices?.length,
    isNotFound,
    limitHasBeenReached,
    remainingActions,
    t,
    theme?.colors,
  ]);

  const actionsLimitTitle = useMemo(() => {
    const hasLimit = actionsLimit?.limit > 0;
    return (
      <S.ActionsLimitTitle>
        <span>
          {`${t('actions-limit.monthly-usage-limit')}${hasLimit ? ':' : ''}`}
        </span>
        {hasLimit && <CustomTag>{actionsLimit?.limit}</CustomTag>}
      </S.ActionsLimitTitle>
    );
  }, [actionsLimit?.limit, t]);

  return {
    actionsLimit,
    actionsLimitTitle,
    actionsLimitContent,
    limitHasBeenReached,
  };
}
